import { Icon } from '@gftg/ui';
import { Link } from 'react-router';
import { Avatar } from '../components/Avatar';
import { PhotoPicker } from '../components/PhotoPicker';
import { LIST_ROW, PageHeader } from '../components/states';
import { TOTAL_STEPS } from '../lib/profileEditor';
import { useMyProfile } from '../lib/queries';
import { useSavedSessions } from '../lib/savedSessions';
import { useSession } from '../lib/session';

/**
 * The Profile tab (PRF-01..08). Only the rows that have something behind them are doors: a settings
 * row that leads nowhere teaches the attendee to distrust the whole list.
 */
export function AccountPage() {
  const { session, signOut, isSigningOut } = useSession();
  const saved = useSavedSessions();
  const profile = useMyProfile();

  if (!session) {
    return null;
  }

  const savedCount = saved.ids.size;
  const me = profile.data;
  const headline = [me?.jobTitle, me?.company]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <PageHeader title="My profile" />

      {/* One column on a phone, two on a laptop: who she is on the left, what she can go and do
          on the right. The order down the page is the same either way, so the phone's reading —
          herself, then how complete it is, then the doors — is what the columns are cut from. */}
      <main className="px-gutter space-y-card pb-6 lg:grid lg:grid-cols-2 lg:items-start lg:gap-card lg:space-y-0">
        <div className="space-y-card">
          <div className="rounded-card border border-border bg-surface p-4">
            <div className="flex items-center gap-3">
              <Avatar
                name={`${me?.firstName ?? session.firstName} ${me?.lastName ?? session.lastName}`.trim()}
                photoAssetId={me?.photoAssetId}
                size="profile"
              />
              <span className="min-w-0">
                <span className="font-display text-card-title block font-semibold text-ink">
                  {`${me?.firstName ?? session.firstName} ${me?.lastName ?? session.lastName}`.trim()}
                </span>
                {headline && <span className="text-subtitle block text-ink-3">{headline}</span>}
                {me?.linkedinUrl && (
                  <span className="text-subtitle block truncate text-ink-3">{me.linkedinUrl}</span>
                )}
                {!headline && session.email && (
                  <span className="text-subtitle block truncate text-ink-3">{session.email}</span>
                )}
              </span>
            </div>

            {/* The only place in the app they can change their photograph (PRF-02). Shown once the
              profile has loaded, because the picker needs to know whether there is one already. */}
            {me && (
              <div className="mt-4 border-t border-border-row pt-4">
                <PhotoPicker profile={me} />
              </div>
            )}
          </div>

          {/* PRF-04: encouragement with a single next step, never a gate. The percentage is the
            server's, so this screen and the organizers' figures cannot disagree about the
            attendee. */}
          {me && (
            <div className="rounded-card border border-border bg-surface p-4">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-row font-semibold text-ink">Profile completeness</p>
                <p className="text-row font-semibold text-brand-text tabular-nums">
                  {me.completion.percent}%
                </p>
              </div>
              <div
                className="mt-3 h-1.5 overflow-hidden rounded-full bg-sand-3"
                role="progressbar"
                aria-valuenow={me.completion.percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Profile completeness"
              >
                <div
                  className="h-full rounded-full bg-brand"
                  style={{ width: `${me.completion.percent}%` }}
                />
              </div>
              {me.completion.nextStep && (
                <p className="text-subtitle mt-2 text-ink-3">{me.completion.nextStep}.</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-card">
          <ul className="rounded-card divide-y divide-border-row overflow-hidden border border-border bg-surface">
            <li>
              <Link to="/account/questionnaire" className={LIST_ROW}>
                <span className="min-w-0 flex-1">
                  <span className="text-row block truncate font-semibold text-ink">
                    {me?.questionnaireCompletedAt
                      ? 'Edit my questionnaire'
                      : 'Finish my questionnaire'}
                  </span>
                  <span className="text-subtitle mt-0.5 block text-ink-3">
                    {me?.questionnaireCompletedAt
                      ? 'Industry, interests, certifications'
                      : me && me.questionnaireStep > 0
                        ? 'Started, and saved as you go'
                        : 'About two minutes'}
                  </span>
                </span>
                <Icon name="chevron-right" size={18} className="shrink-0 text-hairline" />
              </Link>
            </li>
            <li>
              {/* The prototype's "Who can see me" led to the same switches as this row, so there is
                one row rather than two doors into one screen. */}
              <Link to={`/account/questionnaire?step=${TOTAL_STEPS}`} className={LIST_ROW}>
                <span className="min-w-0 flex-1">
                  <span className="text-row block truncate font-semibold text-ink">
                    Visibility and contact
                  </span>
                  <span className="text-subtitle mt-0.5 block text-ink-3">
                    {me
                      ? [
                          me.directoryVisible ? 'In the directory' : 'Hidden from the directory',
                          me.matchingOptIn ? 'in Concierge Connections' : 'not in matches',
                        ].join(', ')
                      : 'Who can see you, and how'}
                  </span>
                </span>
                <Icon name="chevron-right" size={18} className="shrink-0 text-hairline" />
              </Link>
            </li>
            <li>
              <Link to="/schedule" className={LIST_ROW}>
                <span className="min-w-0 flex-1">
                  <span className="text-row block truncate font-semibold text-ink">
                    My schedule
                  </span>
                  <span className="text-subtitle mt-0.5 block text-ink-3">
                    {savedCount === 0
                      ? 'Nothing saved yet'
                      : `${savedCount} saved ${savedCount === 1 ? 'session' : 'sessions'}`}
                  </span>
                </span>
                <Icon name="chevron-right" size={18} className="shrink-0 text-hairline" />
              </Link>
            </li>
            <li>
              <Link to="/directory" className={LIST_ROW}>
                <span className="min-w-0 flex-1">
                  <span className="text-row block truncate font-semibold text-ink">
                    Browse the directory
                  </span>
                  <span className="text-subtitle mt-0.5 block text-ink-3">
                    Everyone attending who is listed
                  </span>
                </span>
                <Icon name="chevron-right" size={18} className="shrink-0 text-hairline" />
              </Link>
            </li>
          </ul>

          <button
            type="button"
            onClick={signOut}
            disabled={isSigningOut}
            className="text-button rounded-button flex min-h-touch w-full items-center justify-center border border-border-input bg-surface px-4 font-semibold text-alert-text disabled:opacity-60"
          >
            {isSigningOut ? 'Signing out…' : 'Sign out'}
          </button>

          {/* ACC-03 is a 90-day session, so signing out is a decision rather than a formality — and
            the starred sessions are this device's, not the session's, so they survive it. */}
          <p className="text-subtitle text-center text-ink-3">
            You will need a new email link to sign in again. The sessions you starred stay on this
            device.
          </p>
        </div>
      </main>
    </>
  );
}
